import React, { useMemo, useState } from 'react';
import { Star, TrendingUp } from 'lucide-react';
import { ReviewCard } from './ReviewCard';
import { ReviewFilters } from './ReviewFilters';
import { WriteReviewModal } from './modals/WriteReviewModal';
import { StarRating } from './StarRating';
interface Review {
  id: string;
  userName: string;
  userInitials: string;
  rating: number;
  date: string;
  verifiedTransaction: boolean;
  reviewText: string;
  sellerResponse?: {
    text: string;
    date: string;
  };
  helpful: number;
}
interface ReviewsSectionProps {
  listingId: string;
  listingTitle: string;
  overallRating: number;
  totalReviews: number;
  reviews: Review[];
}
export function ReviewsSection({
  listingId,
  listingTitle,
  overallRating,
  totalReviews,
  reviews: initialReviews
}: ReviewsSectionProps) {
  const [isWriteReviewOpen, setIsWriteReviewOpen] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [filterByRating, setFilterByRating] = useState<number | null>(null);
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Calculate rating distribution
  const ratingDistribution = useMemo(() => {
    const dist = {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0
    };
    initialReviews.forEach(review => {
      dist[review.rating as keyof typeof dist]++;
    });
    return dist;
  }, [initialReviews]);
  // Filter and sort reviews
  const filteredReviews = useMemo(() => {
    let result = [...initialReviews];
    // Filter by rating
    if (filterByRating) {
      result = result.filter(r => r.rating === filterByRating);
    }
    // Filter by verified only
    if (showVerifiedOnly) {
      result = result.filter(r => r.verifiedTransaction);
    }
    // Filter by search query
    if (searchQuery) {
      result = result.filter(r => r.reviewText.toLowerCase().includes(searchQuery.toLowerCase()) || r.userName.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    // Sort
    if (sortBy === 'newest') {
      result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } else if (sortBy === 'highest') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'lowest') {
      result.sort((a, b) => a.rating - b.rating);
    } else if (sortBy === 'helpful') {
      result.sort((a, b) => b.helpful - a.helpful);
    }
    return result;
  }, [initialReviews, filterByRating, showVerifiedOnly, searchQuery, sortBy]);
  return <div className="space-y-6" data-id="element-1057">
      {/* Overall Rating Summary */}
      <div className="bg-white border border-gray-300 p-6" data-id="element-1058">
        <h2 className="text-2xl font-bold mb-6" data-id="element-1059">Reviews & Ratings</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1060">
          {/* Left: Overall Score */}
          <div className="flex flex-col items-center justify-center p-6 bg-gray-50 border border-gray-200" data-id="element-1061">
            <div className="text-5xl font-bold text-[#1f2933] mb-2" data-id="element-1062">
              {overallRating.toFixed(1)}
            </div>
            <StarRating rating={overallRating} size="lg" showNumber={false} data-id="element-1063" />
            <p className="text-sm text-gray-500 mt-2 font-mono" data-id="element-1064">
              Based on {totalReviews} reviews
            </p>
          </div>

          {/* Right: Rating Distribution */}
          <div className="space-y-2" data-id="element-1065">
            {[5, 4, 3, 2, 1].map(rating => {
            const count = ratingDistribution[rating as keyof typeof ratingDistribution];
            const percentage = totalReviews > 0 ? count / totalReviews * 100 : 0;
            return <div key={rating} className="flex items-center gap-3" data-id="element-1066">
                  <span className="text-sm font-medium w-8" data-id="element-1067">{rating}★</span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1068">
                    <div className="h-full bg-yellow-500" style={{
                  width: `${percentage}%`
                }} data-id="element-1069"></div>
                  </div>
                  <span className="text-xs text-gray-500 w-12 text-right font-mono" data-id="element-1070">
                    {count}
                  </span>
                </div>;
          })}
          </div>
        </div>

        <button onClick={() => setIsWriteReviewOpen(true)} className="w-full mt-6 py-3 bg-[#1f2933] text-white font-bold hover:bg-gray-800" data-id="element-1071">
          Write a Review
        </button>
      </div>

      {/* Search Reviews */}
      <div className="bg-white border border-gray-300 p-4" data-id="element-1072">
        <input type="text" placeholder="Search reviews..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500 text-sm" data-id="element-1073" />
      </div>

      {/* Filters */}
      <ReviewFilters sortBy={sortBy} onSortChange={setSortBy} filterByRating={filterByRating} onRatingFilterChange={setFilterByRating} showVerifiedOnly={showVerifiedOnly} onVerifiedToggle={() => setShowVerifiedOnly(!showVerifiedOnly)} data-id="element-1074" />

      {/* Reviews List */}
      <div className="space-y-4" data-id="element-1075">
        {filteredReviews.length > 0 ? filteredReviews.map(review => <ReviewCard key={review.id} {...review} onHelpful={() => console.log('Marked helpful:', review.id)} onReport={() => console.log('Reported:', review.id)} data-id="element-1076" />) : <div className="bg-white border border-gray-300 p-12 text-center" data-id="element-1077">
            <p className="text-gray-500 font-mono" data-id="element-1078">
              No reviews match your filters
            </p>
          </div>}
      </div>

      <WriteReviewModal isOpen={isWriteReviewOpen} onClose={() => setIsWriteReviewOpen(false)} listingTitle={listingTitle} data-id="element-1079" />
    </div>;
}